import React from 'react';
import {
  IconCheck,
  IconUpload,
  IconStar,
  IconZap,
  IconShield,
} from '../components/Icons';
import { PRODUCTS, SOLUTIONS, anchorFor, productById } from './catalog';
import { goToSection } from './MarketingNav';

const EXTRAS = [
  {
    icon: <IconStar size={20} />,
    title: 'Email summaries',
    text:
      'Emailed risk summaries after bulk runs, so you and your trading partner stay in step on consistency exposure.',
  },
  {
    icon: <IconZap size={20} />,
    title: 'Roll-week & event markers',
    text:
      'Markers for Nasdaq futures roll weeks and high-impact US economic events, so you know when history can mislead.',
  },
  {
    icon: <IconShield size={20} />,
    title: 'Secure by default',
    text:
      'Google and Microsoft sign-in, read-only broker connections and Whop billing. RiskLo never places trades.',
  },
];

// Which mock visual each product shows.
const VISUAL = {
  ai: 'ai',
  portfolio: 'portfolio',
  'prop-risk': 0,
  'portfolio-risk': 'portfolio-risk',
  simulator: 3,
  results: 2,
  dashboards: 7,
  'trade-watcher': 'trade-watcher',
};

const TAG_CLASS = { Pro: 'pro', Free: 'free', New: 'new', Companies: 'companies' };

function FeaturesPage({ onNavigate, user }) {
  const go = (page) => () => onNavigate && onNavigate(page);
  const isSignedIn = Boolean(user?.email);

  return (
    <>
      <section className="marketing-page-header">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-eyebrow marketing-reveal">Products</div>
          <h1 className="marketing-title marketing-reveal" data-delay="1">
            Everything RiskLo does, <span className="accent">in one place.</span>
          </h1>
          <p className="marketing-subtitle marketing-reveal" data-delay="2" style={{ margin: '1rem auto 0' }}>
            Connect your accounts, check every setup against your prop firm’s rules, combine strategies before you
            go live, and ask RiskLo AI about your own numbers.
          </p>
          <div className="mf-jump marketing-reveal" data-delay="3">
            {PRODUCTS.map((p) => (
              <button key={p.id} type="button" onClick={() => goToSection(null, null, anchorFor('product', p.id))}>
                {p.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section" style={{ paddingTop: 0 }}>
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">Solutions</div>
            <h2 className="marketing-title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.6rem)' }}>Built for the way you trade.</h2>
          </div>
          <div className="mf-solutions">
            {SOLUTIONS.map((sol, i) => (
              <div key={sol.id} id={anchorFor('solution', sol.id)} className={`mf-solution tone-${sol.tone} marketing-reveal`} data-delay={i + 1}>
                <span className={`mnav-icon tone-${sol.tone}`}>{sol.icon}</span>
                <h3>{sol.title}</h3>
                <p>{sol.text}</p>
                <div className="mf-solution-links">
                  {sol.products.map((pid) => (
                    <button key={pid} type="button" onClick={() => goToSection(null, null, anchorFor('product', pid))}>
                      {productById(pid)?.title} →
                    </button>
                  ))}
                  {sol.page ? (
                    <button type="button" className="is-strong" onClick={go(sol.page)}>Company plans →</button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section" style={{ paddingTop: 0 }}>
        <div className="marketing-container">
          {PRODUCTS.map((p, i) => {
            const reverse = i % 2 === 1;
            return (
              <div className={`marketing-split${reverse ? ' reverse' : ''}`} key={p.id} id={anchorFor('product', p.id)}>
                <div className="marketing-split-text marketing-reveal">
                  <div className="marketing-eyebrow">
                    {p.icon} {p.tag === 'Companies' ? 'For companies' : p.tag === 'Free' ? 'Free on every plan' : p.tag === 'New' ? 'New on Pro' : 'Pro'}
                  </div>
                  <h3 className="marketing-split-title">
                    <span>{p.title}</span>
                    <span className={`marketing-title-pill ${TAG_CLASS[p.tag] || 'pro'}`}>{p.tag}</span>
                  </h3>
                  <p>{p.text}</p>
                  <ul className="marketing-split-list">
                    {p.bullets.map((b) => (
                      <li key={b}><IconCheck size={16} /> {b}</li>
                    ))}
                  </ul>
                </div>
                <div className="marketing-split-visual marketing-reveal" data-delay="1">
                  <FeatureVisual id={VISUAL[p.id]} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="marketing-section tight">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">More built in</div>
            <h2 className="marketing-title">Details traders notice.</h2>
          </div>
          <div className="marketing-feature-grid">
            {EXTRAS.map((e, i) => (
              <div key={e.title} className="marketing-feature-card marketing-reveal" data-delay={i + 1}>
                <div className="marketing-feature-icon">{e.icon}</div>
                <h3 className="marketing-feature-title">{e.title}</h3>
                <p className="marketing-feature-text">{e.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-cta marketing-reveal">
            <div className="marketing-cta-inner">
              <h2>Ready to check your next trade?</h2>
              <p>Start free, then try Pro free for 7 days.</p>
              <div className="marketing-hero-cta-row" style={{ marginTop: 0 }}>
                <button type="button" className="marketing-btn marketing-btn-lg" onClick={go(isSignedIn ? 'results' : 'login')}>
                  {isSignedIn ? 'Open dashboard' : 'Get started free'}
                </button>
                <button type="button" className="marketing-btn-ghost marketing-btn-lg" onClick={go('pricing')}>
                  Compare plans
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/** Mock chat for RiskLo AI (illustrative, no real data). */
export function AiVisual() {
  return (
    <div className="marketing-mock-card mf-ai">
      <div className="mf-ai-msg is-user">How did my accounts do this week?</div>
      <div className="mf-ai-msg">
        Across your 4 accounts: <strong className="up">+$1,284</strong> this week, 4 of 5 days green.
        Best day Tue (+$910). Your Apex 50K is 62% of the way to its drawdown limit.
      </div>
      <div className="mf-ai-msg is-user">What’s on the calendar tomorrow?</div>
      <div className="mf-ai-msg">
        8:30am ET: CPI. 2:00pm: FOMC minutes. After the close: NVDA earnings.
      </div>
      <div className="mf-ai-input">Ask anything about your trading…</div>
    </div>
  );
}

function AccountsVisual() {
  const rows = [
    ['NinjaTrader · Apex 50K', '$51,284', '+2.6%'],
    ['Schwab · Brokerage', '$84,910', '+1.1%'],
    ['Coinbase', '$12,430', '−0.8%'],
    ['Alpaca · Paper', '$100,520', '+0.5%'],
  ];
  return (
    <div className="marketing-mock-card mf-accounts">
      <div className="marketing-mock-label">My Portfolio · 4 accounts</div>
      <div className="mf-accounts-total">$249,144 <span className="up">+1.4% this month</span></div>
      {rows.map(([name, value, ch]) => (
        <div key={name} className="mf-accounts-row">
          <span>{name}</span>
          <span>{value}</span>
          <span className={ch.startsWith('−') ? 'down' : 'up'}>{ch}</span>
        </div>
      ))}
    </div>
  );
}

function ConcentrationVisual() {
  const bars = [['NVDA', 28], ['AAPL', 17], ['QQQ', 14], ['BTC', 11], ['Cash', 9]];
  return (
    <div className="marketing-mock-card">
      <div className="marketing-mock-label">Concentration · acts like 6.2 positions</div>
      {bars.map(([sym, pct]) => (
        <div key={sym} className="mf-bar-row">
          <span>{sym}</span>
          <div className="marketing-mock-bar" style={{ '--w': `${pct * 3}%` }}><span /></div>
          <span>{pct}%</span>
        </div>
      ))}
      <div style={{ marginTop: '0.7rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.86rem' }}>
        If everything fell 10%: −$24,914
      </div>
    </div>
  );
}

function WatcherVisual() {
  return (
    <div className="marketing-mock-card mf-watch">
      <div className="marketing-mock-label">Trade Watcher</div>
      {[
        ['APEX-50K-01', 'Connected', 'ok'],
        ['APEX-50K-02', 'Naked position: 2 NQ long, no stop', 'bad'],
        ['TOPSTEP-100K', 'Connected', 'ok'],
      ].map(([acct, state, tone]) => (
        <div key={acct} className={`mf-watch-row is-${tone}`}>
          <span className="mf-watch-dot" />
          <span>{acct}</span>
          <span>{state}</span>
        </div>
      ))}
      <div style={{ marginTop: '0.7rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.86rem' }}>
        Alert sent in 3s · auto-closed after 10s
      </div>
    </div>
  );
}

function FeatureVisual({ id }) {
  // Decorative mock visuals — purely illustrative, no real data.
  if (id === 'ai') return <AiVisual />;
  if (id === 'portfolio') return <AccountsVisual />;
  if (id === 'portfolio-risk') return <ConcentrationVisual />;
  if (id === 'trade-watcher') return <WatcherVisual />;
  if (id === 0) {
    // Blowout
    return (
      <div>
        <div className="marketing-mock-toolbar" style={{ borderRadius: 12, marginBottom: 12 }}>
          <span className="marketing-mock-dot" />
          <span className="marketing-mock-dot" />
          <span className="marketing-mock-dot" />
          <span className="marketing-mock-pill">Blowout Risk</span>
        </div>
        <div className="marketing-mock-card" style={{ textAlign: 'center' }}>
          <div className="marketing-mock-label">Account blowout risk</div>
          <div className="marketing-mock-value go" style={{ fontSize: '2.4rem' }}>GO</div>
          <div className="marketing-mock-bar" style={{ '--w': '24%' }}><span /></div>
          <div style={{ marginTop: '0.75rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
            EOD blowout probability: 4.1%
          </div>
        </div>
      </div>
    );
  }
  if (id === 1) {
    // % Rule
    return (
      <div className="marketing-mock-card">
        <div className="marketing-mock-label">Consistency limit (50%)</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
          <div className="marketing-mock-value" style={{ color: '#34d399' }}>SAFE</div>
          <div className="marketing-mock-bar" style={{ flex: 1, '--w': '42%' }}><span /></div>
        </div>
        <div style={{ marginTop: '0.75rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
          Worst day −$420 vs limit −$1,000
        </div>
      </div>
    );
  }
  if (id === 2 || id === 3) {
    // Vector results / Portfolio
    return (
      <div className="marketing-mock-card" style={{ background: 'rgba(0,0,0,0.2)' }}>
        <div className="marketing-mock-label">{id === 2 ? 'Strategy weekly' : 'Portfolio equity'}</div>
        <svg className="marketing-mock-chart" viewBox="0 0 600 160" preserveAspectRatio="none">
          <defs>
            <linearGradient id={`fl${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(129, 140, 248, 0.4)" />
              <stop offset="100%" stopColor="rgba(129, 140, 248, 0)" />
            </linearGradient>
            <linearGradient id={`ln${id}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#818cf8" />
              <stop offset="100%" stopColor="#c4b5fd" />
            </linearGradient>
          </defs>
          <path
            fill={`url(#fl${id})`}
            d="M0,130 L60,120 L120,95 L180,108 L240,80 L300,68 L360,72 L420,40 L480,52 L540,30 L600,20 L600,160 L0,160 Z"
          />
          <path
            stroke={`url(#ln${id})`}
            strokeWidth="2.5"
            fill="none"
            d="M0,130 L60,120 L120,95 L180,108 L240,80 L300,68 L360,72 L420,40 L480,52 L540,30 L600,20"
          />
        </svg>
      </div>
    );
  }
  if (id === 4) {
    // Bulk table
    return (
      <div className="marketing-mock-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead style={{ background: 'rgba(255,255,255,0.04)' }}>
            <tr>
              {['#','Account','Strategy','Size','State'].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '0.55rem 0.7rem', fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              [1,'PAAPEX...01','Grass Fed','$50K','GO'],
              [2,'PAAPEX...02','Tactical','$100K','GO'],
              [3,'PAAPEX...03','Momentum','$50K','NO GO'],
              [4,'PAAPEX...04','Sniper','$25K','GO'],
              [5,'PAAPEX...05','Vault','$150K','GO'],
              [6,'PAAPEX...06','Reaper','$50K','NO GO'],
            ].map((row) => (
              <tr key={row[0]} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                {row.map((c, j) => (
                  <td
                    key={j}
                    style={{
                      padding: '0.5rem 0.7rem',
                      color: j === 4 ? (c === 'GO' ? '#34d399' : '#ef4444') : 'rgba(255,255,255,0.78)',
                      fontWeight: j === 4 ? 700 : 500,
                    }}
                  >{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (id === 5) {
    // CSV
    return (
      <div className="marketing-mock-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center', border: '1px dashed rgba(255,255,255,0.16)' }}>
        <IconUpload size={36} />
        <div className="marketing-mock-value" style={{ marginTop: '0.75rem', fontSize: '1.1rem' }}>Drop NinjaTrader CSV</div>
        <div style={{ color: 'rgba(255,255,255,0.55)', marginTop: '0.4rem' }}>Accounts + Strategies auto-detected</div>
      </div>
    );
  }
  if (id === 6) {
    // Vector Capital strategies
    return (
      <div className="marketing-mock-card" style={{ background: 'rgba(0,0,0,0.2)' }}>
        <div className="marketing-mock-label">Growth vs benchmarks</div>
        <svg className="marketing-mock-chart" viewBox="0 0 600 160" preserveAspectRatio="none">
          <path stroke="#34d399" strokeWidth="2.5" fill="none" d="M0,140 L60,128 L120,108 L180,118 L240,82 L300,90 L360,60 L420,52 L480,30 L540,24 L600,10" />
          <path stroke="#818cf8" strokeWidth="2.5" fill="none" d="M0,140 L60,130 L120,120 L180,108 L240,100 L300,92 L360,84 L420,70 L480,62 L540,50 L600,42" />
          <path stroke="#94a3b8" strokeWidth="2" fill="none" strokeDasharray="4 4" d="M0,140 L60,134 L120,128 L180,124 L240,120 L300,114 L360,106 L420,98 L480,90 L540,82 L600,76" />
        </svg>
        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#34d399', borderRadius: 2, marginRight: 6 }} />Portfolio</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#818cf8', borderRadius: 2, marginRight: 6 }} />NQ</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#94a3b8', borderRadius: 2, marginRight: 6 }} />SPY</span>
        </div>
      </div>
    );
  }
  // id === 7 — Custom dashboards
  return (
    <div className="marketing-mock-card">
      <div className="marketing-mock-toolbar" style={{ borderRadius: 10, marginBottom: 10, background: 'transparent', borderBottom: 'none', padding: 0 }}>
        <span className="marketing-mock-pill">Custom dashboard</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        <div className="marketing-mock-card" style={{ padding: '0.7rem' }}>
          <div className="marketing-mock-label" style={{ fontSize: '0.65rem' }}>Portfolio P&L</div>
          <div className="marketing-mock-value" style={{ fontSize: '1rem', color: '#34d399' }}>+$1,840</div>
        </div>
        <div className="marketing-mock-card" style={{ padding: '0.7rem' }}>
          <div className="marketing-mock-label" style={{ fontSize: '0.65rem' }}>Risk score</div>
          <div className="marketing-mock-value" style={{ fontSize: '1rem' }}>Low</div>
        </div>
        <div className="marketing-mock-card" style={{ padding: '0.7rem', gridColumn: 'span 2' }}>
          <div className="marketing-mock-label" style={{ fontSize: '0.65rem' }}>Equity curve</div>
          <svg className="marketing-mock-chart" viewBox="0 0 600 80" preserveAspectRatio="none" style={{ height: 50 }}>
            <path
              stroke="#818cf8"
              strokeWidth="2.5"
              fill="none"
              d="M0,68 L60,60 L120,52 L180,56 L240,42 L300,48 L360,30 L420,34 L480,18 L540,22 L600,10"
            />
          </svg>
        </div>
        <div className="marketing-mock-card" style={{ padding: '0.7rem' }}>
          <div className="marketing-mock-label" style={{ fontSize: '0.65rem' }}>Widgets</div>
          <div className="marketing-mock-value" style={{ fontSize: '1rem' }}>6 pinned</div>
        </div>
        <div className="marketing-mock-card" style={{ padding: '0.7rem' }}>
          <div className="marketing-mock-label" style={{ fontSize: '0.65rem' }}>Layouts</div>
          <div className="marketing-mock-value" style={{ fontSize: '1rem' }}>3 saved</div>
        </div>
      </div>
    </div>
  );
}

export default FeaturesPage;
